import { Link } from "react-router-dom";
import Avatar from "@/components/base/Avatar";
import { topics } from "@/mocks/topics";
import { formatViews } from "@/utils/format";

const tones = ["primary", "accent", "secondary"] as const;

export default function DiscussionSection() {
  const feed = topics
    .flatMap((topic) =>
      topic.discussion.map((item) => ({
        ...item,
        topicId: topic.id,
        topicTitle: topic.title,
        categoryName: topic.categoryName,
      })),
    )
    .slice(0, 6);

  const ranking = [...topics].sort((a, b) => b.heat - a.heat).slice(0, 6);

  return (
    <section className="w-full bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between gap-4">
              <div>
                <span className="flex items-center gap-2 text-xs font-medium tracking-wide text-accent-700">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-signal-tower-line text-base" />
                  </span>
                  实时连线
                </span>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  讨论区热议
                </h2>
              </div>
              <span className="flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1.5 text-xs text-primary-700">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500" />
                </span>
                进行中
              </span>
            </div>

            <ul className="mt-6 flex flex-col gap-3">
              {feed.map((item, index) => (
                <li
                  key={`${item.topicId}-${item.id}`}
                  className="rounded-card border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-200 md:p-5"
                >
                  <div className="flex items-start gap-3">
                    <Avatar name={item.user} tone={tones[index % tones.length]} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-medium text-foreground-900">{item.user}</span>
                        <span className="text-xs text-foreground-400">{item.handle}</span>
                        {item.badge && (
                          <span className="rounded-md bg-secondary-100 px-1.5 py-0.5 text-[10px] font-medium text-secondary-800">
                            {item.badge}
                          </span>
                        )}
                        <span className="text-xs text-foreground-400">{item.time}</span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                        <Link
                          to={`/topic/${item.topicId}`}
                          className="flex items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-primary-600"
                        >
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-discuss-line text-sm" />
                          </span>
                          来自「{item.topicTitle.length > 18 ? `${item.topicTitle.slice(0, 18)}…` : item.topicTitle}」
                        </Link>
                        <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-thumb-up-line text-sm" />
                          </span>
                          {item.likes}
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 rounded-card border border-background-200 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-secondary-600">
                  <i className="ri-bar-chart-2-line text-lg" />
                </span>
                瓜田榜单
              </h3>
              <p className="mt-1.5 text-xs text-foreground-500">按讨论热度排序，每小时更新</p>
              <ol className="mt-5 flex flex-col gap-1">
                {ranking.map((topic, index) => (
                  <li key={topic.id}>
                    <Link
                      to={`/topic/${topic.id}`}
                      className="flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-background-50"
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                          index < 3
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-2 text-sm text-foreground-800">
                          {topic.title}
                        </span>
                        <span className="mt-1 flex items-center gap-2 text-[11px] text-foreground-500">
                          <span>{topic.categoryName}</span>
                          <span className="text-foreground-300">·</span>
                          <span>{formatViews(topic.views)} 阅读</span>
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
              <Link
                to="/category/variety"
                className="mt-4 flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                查看更多榜单
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}